/**
 * AuthContext.jsx — Global authentication state for the M16S app.
 *
 * Provides:
 *   user           {object|null}   — The current user's profile data from /auth/me.
 *                                    Null when not authenticated.
 *   loading        {boolean}       — True while the initial token validation is in
 *                                    progress. Guards should wait for this to be
 *                                    false before making redirect decisions.
 *   isAuthenticated {boolean}      — True once a valid token and user profile have
 *                                    been confirmed.
 *   login(username, password)      — Calls POST /auth/login, stores the returned JWT
 *                                    in localStorage, then fetches and stores the user
 *                                    profile. Returns the profile data.
 *   logout()                       — Clears the JWT from localStorage and resets all
 *                                    auth state. Does NOT make a server-side call.
 *   isAdmin()      {() => boolean} — Returns true if user.role === 'admin'.
 *   isApproved()   {() => boolean} — Returns true if user.status === 'approved'.
 *
 * Usage:
 *   Wrap your tree with <AuthProvider>. Consume via the useAuth() hook anywhere
 *   inside that tree.
 */
import { createContext, useContext, useEffect, useState } from 'react';
import { authAPI } from '../services/api';

const AuthContext = createContext();

export function AuthProvider({ children }) {
  // user: the full profile object returned by GET /auth/me, or null if not logged in.
  const [user, setUser] = useState(null);
  // loading: true until the initial token check resolves (prevents flash of wrong route).
  const [loading, setLoading] = useState(true);
  // isAuthenticated: flipped to true once a valid token + profile are confirmed.
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // On mount: if a JWT exists in localStorage, validate it by fetching the user profile.
  // If the token is missing or the request fails (expired/invalid), the token is removed
  // and the user stays unauthenticated.
  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const response = await authAPI.getProfile();
          setUser(response.data);
          setIsAuthenticated(true);
        } catch (error) {
          // Token is invalid or expired
          localStorage.removeItem('token');
          setUser(null);
          setIsAuthenticated(false);
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  /**
   * Authenticates the user with the backend.
   * Stores the returned JWT in localStorage and fetches the user profile.
   * @param {string} username
   * @param {string} password
   * @returns {Promise<object>} The user profile data.
   */
  const login = async (username, password) => {
    const response = await authAPI.login(username, password);
    localStorage.setItem('token', response.data.access_token);

    // Fetch user profile
    const profileResponse = await authAPI.getProfile();
    setUser(profileResponse.data);
    setIsAuthenticated(true);

    return profileResponse.data;
  };

  /**
   * Logs the user out by clearing the JWT from localStorage and resetting state.
   * The caller is responsible for redirecting away from protected pages.
   */
  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
    setIsAuthenticated(false);
  };

  /**
   * Returns true if the current user has the 'admin' role.
   * @returns {boolean}
   */
  const isAdmin = () => {
    return user?.role === 'admin';
  };

  /**
   * Returns true if the current user's account has been approved by an admin.
   * @returns {boolean}
   */
  const isApproved = () => {
    return user?.status === 'approved';
  };

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      isAuthenticated,
      login,
      logout,
      isAdmin,
      isApproved,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

/**
 * useAuth — Hook to access authentication context.
 * Must be called inside a component that is a descendant of <AuthProvider>.
 * Throws if used outside the provider tree.
 * @returns {{ user, loading, isAuthenticated, login, logout, isAdmin, isApproved }}
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
